/**
 * `GET /admin/tenants/:tenantId/usage` — slice 71. How much of Flow and of the
 * rule segments a workspace has built, as the staff console's tenant page
 * shows it.
 *
 * ## WHY COUNTS AND NOTHING ELSE
 *
 * Slice 8's boundary (`TENANT_DATA_ADMIN_MAY_NOT_REACH` in `./admin.js`) says
 * no admin route reads or writes a workspace's own data — its contacts,
 * segments, campaigns and the rest. Staff still need one fact to support a
 * workspace that has just been given Flow: *has it built anything?* A
 * "flow is on, nothing launched" call is a different conversation from "two
 * flows live, one paused". So this read carries **counts only**: how many
 * flows in each status and how many rule segments, and no name, no graph, no
 * member, no address and no id of any of them. The shape is `.strict()` so a
 * later "and the names, for support" fails parsing here rather than slipping
 * through a response.
 *
 * The rejected alternative was to put the counts on `tenantSchema`, which
 * every admin read returns: that would have made the tenant list (one row per
 * workspace) run four extra aggregates per page for a figure only the detail
 * page shows, and would have tied the most-read admin shape to two tables the
 * boundary otherwise keeps at arm's length.
 *
 * ## COMPUTED ON READ
 *
 * Never stored. `generatedAt` says when, so the page can say "counted at".
 * A flow's placeholder segment (`segments.flow_id` set, slice 68) is the
 * engine's bookkeeping, not a segment anybody built, and is not counted.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';

const countSchema = z.number().int().min(0);

export const tenantUsageResponseSchema = z
  .object({
    flows: z
      .object({
        live: countSchema,
        paused: countSchema,
        draft: countSchema,
        archived: countSchema,
      })
      .strict(),
    /** Rule (dynamic) segments the workspace saved — never a flow's hidden placeholder. */
    ruleSegments: countSchema,
    generatedAt: isoDateTimeSchema,
  })
  .strict();
export type TenantUsageResponse = z.infer<typeof tenantUsageResponseSchema>;
